import * as XLSX from 'xlsx';
import type { CustomColumnDef } from '../../../../components/Table/types';
import { formatSummaryMatchCellValue } from './buildSummaryMatchGridColumns';

type ExportSummaryMatchTableParams = {
  rows: Record<string, string>[];
  columns: CustomColumnDef[];
  extension: 'tsv' | 'xlsx';
  view: string;
  tab: string;
  source: string;
};

function escapeTsvCell(value: unknown): string {
  const raw = value == null || value === '' ? '-' : String(value);
  if (raw.includes('\t') || raw.includes('\n') || raw.includes('"')) {
    return `"${raw.replace(/"/g, '""')}"`;
  }
  return raw;
}

function getHeaderLabel(column: CustomColumnDef): string {
  if (typeof column.header === 'string') return column.header;
  return column.name;
}

function getCellValue(row: Record<string, string>, column: CustomColumnDef): string {
  return formatSummaryMatchCellValue(row[column.name]);
}

function buildFileName({ view, tab, source, extension }: ExportSummaryMatchTableParams): string {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `${view}_SummaryMatch_${tab}_${source}_${stamp}.${extension}`;
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportSummaryMatchTable(params: ExportSummaryMatchTableParams): void {
  const { rows, columns, extension } = params;
  if (!rows.length || !columns.length) return;

  const fileName = buildFileName(params);

  if (extension === 'tsv') {
    const headerLine = columns.map(col => escapeTsvCell(getHeaderLabel(col))).join('\t');
    const dataLines = rows.map(row => columns.map(col => escapeTsvCell(getCellValue(row, col))).join('\t'));
    const content = [headerLine, ...dataLines].join('\n');
    triggerDownload(new Blob([content], { type: 'text/tab-separated-values;charset=utf-8' }), fileName);
    return;
  }

  const sheetRows = rows.map(row => {
    const excelRow: Record<string, string> = {};
    columns.forEach(col => {
      excelRow[getHeaderLabel(col)] = getCellValue(row, col);
    });
    return excelRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(sheetRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary match');
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  triggerDownload(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    fileName,
  );
}
